import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Award, ArrowLeft } from 'lucide-react';
import NECSLogo from '../components/NECSLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificateVerify() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/training/verify/${encodeURIComponent(certId)}`);
        const json = await res.json();
        setData(json);
      } catch (_e) {
        setData({ valid: false, reason: 'Network error' });
      }
      setLoading(false);
    })();
  }, [certId]);

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <NECSLogo variant="default" size="md" align="center" />
        </div>

        {loading ? (
          <Card className="bg-zinc-900 border-zinc-700 p-8 text-center">
            <p className="text-zinc-400">Verifying certificate…</p>
          </Card>
        ) : data?.valid ? (
          <Card
            className="p-8 text-center"
            style={{
              background: 'radial-gradient(circle at 50% 0%, rgba(201,169,98,0.18), transparent 70%), #1a1a1a',
              border: '2px solid #C9A962',
            }}
            data-testid="certificate-verify-valid"
          >
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#C9A962' }}>
              <CheckCircle2 className="w-12 h-12 text-[#1a1a1a]" strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl font-black mb-1" style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif' }}>
              CERTIFICATE VERIFIED
            </h1>
            <p className="text-sm text-zinc-400 mb-8">Issued by Noble Empire Cleaning Services, LLC</p>

            <div className="text-left bg-[#222] rounded-xl p-6 border border-white/10 space-y-3">
              <Field label="Holder" value={data.user_name} />
              <Field label="Role" value={(data.role || '').replace('_', ' ').toUpperCase()} />
              {data.badge_number && <Field label="Badge" value={data.badge_number} />}
              {data.property_name && <Field label="Property" value={data.property_name} />}
              <Field
                label="Issued"
                value={data.issued_at ? new Date(data.issued_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
              />
              <Field label="Certificate ID" value={data.certificate_id} mono />

              {data.modules?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 mt-3">Completed Modules</p>
                  <ul className="space-y-1">
                    {data.modules.map((m, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-200">
                        <Award className="h-3.5 w-3.5 text-[#C9A962]" /> {m.title}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <p className="text-xs text-zinc-500 mt-6">
              This certificate is authentic and was issued by Noble Empire Cleaning Services, LLC, Flower Mound, TX.
            </p>
          </Card>
        ) : (
          <Card className="bg-zinc-900 border border-red-500/40 p-8 text-center" data-testid="certificate-verify-invalid">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center bg-red-500/20">
              <XCircle className="w-12 h-12 text-red-500" strokeWidth={2} />
            </div>
            <h1 className="text-3xl font-black text-red-400 mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              CERTIFICATE NOT FOUND
            </h1>
            <p className="text-sm text-zinc-400 mb-2">
              No certificate matches the ID <span className="font-mono text-zinc-300">{certId}</span>.
            </p>
            <p className="text-xs text-zinc-500">
              Reason: {data?.reason || 'unknown'}
            </p>
          </Card>
        )}

        <div className="text-center mt-6">
          <Button
            variant="outline"
            className="border-[#C9A962] text-[#C9A962]"
            onClick={() => { window.location.href = '/'; }}
            data-testid="certificate-verify-home"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to home
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className={`text-sm text-white ${mono ? 'font-mono' : 'font-semibold'}`}>{value || '—'}</span>
    </div>
  );
}
